/**
 * Markdown Normalization Utilities for Chatbot Responses
 *
 * LLMs (Large Language Models) use tokenization which can cause formatting issues
 * in their output. Common problems include missing spaces before/after markdown
 * links and between words and numbers. These utilities fix those issues before
 * rendering the markdown to HTML.
 *
 * Used by:
 * - chatbot_base_controller.js (streaming chatbot UI)
 * - chatbot_conversation_controller.js (conversation history display)
 */

/**
 * Common English words that should have a space before numbers.
 * LLMs sometimes omit spaces between these words and following digits
 * due to tokenization boundaries (e.g., "the2026" instead of "the 2026").
 */
const WORDS_REQUIRING_SPACE_BEFORE_NUMBERS = [
  // Articles & prepositions
  'the', 'a', 'an', 'for', 'in', 'to', 'by', 'at', 'on', 'of', 'from', 'with',
  // Verbs
  'is', 'are', 'was', 'were', 'be',
  // Comparisons
  'than', 'over', 'under', 'about',
  // Months (for dates like "December12th")
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
  // Seasons (for semesters like "Spring2026")
  'Spring', 'Summer', 'Fall', 'Winter',
]

/**
 * Pre-compiled regex for word-number spacing (built once at module load for performance).
 *
 * Pattern: (^|\s)(the|a|an|...)(\d)
 *   - (^|\s)  = Capture group 1: start of string OR whitespace
 *               Using this instead of \b (word boundary) to avoid matching inside
 *               URLs like https://for2024.com where "/" creates a word boundary
 *   - (word)  = Capture group 2: one of the common words from the list
 *   - (\d)    = Capture group 3: a digit
 *
 * Replacement: '$1$2 $3' preserves the leading whitespace, the word, adds a space, then the digit
 */
const WORD_NUMBER_SPACING_REGEX = new RegExp(
  `(^|\\s)(${WORDS_REQUIRING_SPACE_BEFORE_NUMBERS.join('|')})(\\d)`,
  'gi',
)

/**
 * Normalize markdown text to fix common LLM formatting issues.
 *
 * Fixes applied:
 * 1. Space before markdown links (LLM sometimes omits them)
 * 2. Space after markdown links before word characters
 * 3. Space between common words and numbers (e.g., "the2026" -> "the 2026")
 * 4. Space before numbered list items (e.g., "University1." -> "University 1.")
 * 5. Newline before inline numbered lists (so markdown renders them as actual lists)
 * 6. Space after comma in dates (e.g., "January 31,2026" -> "January 31, 2026")
 * 7. Newline before inline bullet points (so markdown renders them as actual lists)
 * 8. Convert empty links to bold text (e.g., "[Text]()" -> "**Text**")
 *
 * @param {string} text - Raw markdown text from LLM response
 * @returns {string} Normalized markdown text ready for rendering
 */
export function normalizeMarkdown(text) {
  if (!text) return text
  return (
    text
      /**
       * Fix 1: Ensure space before markdown links
       *
       * Pattern: ([^\s!*])\[
       *   - ([^\s!*]) = Capture any character that is NOT:
       *                 \s = whitespace (already has space)
       *                 !  = exclamation (image syntax: ![alt](url))
       *                 *  = asterisk (bold/italic: **text**[link])
       *   - \[       = Opening bracket of markdown link
       *
       * Example: "click[here](url)" -> "click [here](url)"
       */
      .replace(/([^\s!*])\[/g, '$1 [')

      /**
       * Fix 2: Ensure space after markdown links before words
       *
       * Pattern: \]\(([^)]+)\)(\w)
       *   - \]       = Closing bracket of link text
       *   - \(       = Opening paren of URL
       *   - ([^)]+)  = Capture group 1: URL (any chars except closing paren)
       *   - \)       = Closing paren of URL
       *   - (\w)     = Capture group 2: word character immediately after
       *
       * Example: "[link](url)next" -> "[link](url) next"
       *
       * Note: URLs with nested parens (e.g., Wikipedia) may not work perfectly
       */
      .replace(/\]\(([^)]+)\)(\w)/g, ']($1) $2')

      /**
       * Fix 3: Ensure space between common words and numbers
       *
       * Uses pre-compiled WORD_NUMBER_SPACING_REGEX (see above for pattern details)
       *
       * Example: "the2026-2027 school year" -> "the 2026-2027 school year"
       */
      .replace(WORD_NUMBER_SPACING_REGEX, '$1$2 $3')

      /**
       * Fix 4: Ensure space before numbered list items
       *
       * Pattern: ([a-zA-Z])(\d+\.)(?=\s)
       *   - ([a-zA-Z]) = Capture group 1: a letter (not digit/underscore)
       *   - (\d+\.)    = Capture group 2: one or more digits followed by period
       *   - (?=\s)     = Lookahead: must be followed by whitespace (list formatting)
       *
       * Example: "University1. **Federal" -> "University 1. **Federal"
       * Skips:   "Form1.pdf" (no space after period)
       *          "Section2.1" (digit after period, not space)
       *          "3.14" (digit before, not letter)
       */
      .replace(/([a-zA-Z])(\d+\.)(?=\s)/g, '$1 $2')

      /**
       * Fix 5: Ensure newline before inline numbered lists
       *
       * Pattern: ([^\n])(\s*)(\d+\.)\s+(?=\*|\[)
       *   - ([^\n])   = Capture group 1: any char that's NOT a newline (meaning list is inline)
       *   - (\s*)     = Capture group 2: optional whitespace (consumed but not preserved)
       *   - (\d+\.)   = Capture group 3: numbered list indicator (1., 2., etc.)
       *   - \s+       = One or more whitespace chars after the number
       *   - (?=\*|\[) = Lookahead: must be followed by * (bold) or [ (link)
       *
       * Example: "University 1. **Federal" -> "University\n\n1. **Federal"
       *
       * This ensures markdown parsers recognize numbered lists (they must start on new lines)
       */
      .replace(/([^\n])(\s*)(\d+\.)\s+(?=\*|\[)/g, '$1\n\n$3 ')

      /**
       * Fix 6: Ensure space after comma in dates
       *
       * Pattern: (\d{1,2}),((?:19|20)\d{2})\b
       *   - (\d{1,2})       = Capture group 1: 1-2 digits (day of month: 1-31)
       *   - ,               = Literal comma
       *   - ((?:19|20)\d{2}) = Capture group 2: 4-digit year starting with 19 or 20
       *   - \b              = Word boundary (ensures we match the full year)
       *
       * Example: "January 31,2026" -> "January 31, 2026"
       *
       * This specifically targets date patterns without affecting thousand separators (e.g., $1,500)
       */
      .replace(/(\d{1,2}),((?:19|20)\d{2})\b/g, '$1, $2')

      /**
       * Fix 7: Ensure newline before inline bullet points
       *
       * Pattern: ([^\s\n])(-)(\s+)(?=\*|\[)
       *   - ([^\s\n-]) = Capture group 1: non-whitespace, non-dash char before the dash
       *                  (the non-dash exclusion prevents matching inside a `---` horizontal rule)
       *   - (-)       = Capture group 2: the bullet dash
       *   - (\s+)     = Capture group 3: whitespace after the dash
       *   - (?=\*|\[) = Lookahead: must be followed by * (bold) or [ (link)
       *
       * Example: "Tips- **Start Early**" -> "Tips\n\n- **Start Early**"
       * Skips:   "Spring 2026 - **Registration**" (space before dash = intentional prose)
       *
       * This ensures markdown parsers recognize bullet lists (they must start on new lines)
       */
      .replace(/([^\s\n-])(-)(\s+)(?=\*|\[)/g, '$1\n\n$2$3')

      /**
       * Fix 8: Convert empty links to bold text
       *
       * Pattern: \[([^\]]+)\]\(\s*#?\s*\)
       *   - \[([^\]]+)\] = Capture group 1: link text (one or more non-bracket chars)
       *   - \(           = Opening paren of URL
       *   - \s*#?\s*     = Optional whitespace and/or bare "#" (common empty href patterns)
       *   - \)           = Closing paren of URL
       *
       * Example: "[Funnel Snapshot]()" -> "**Funnel Snapshot**"
       *          "[Link Text](#)"      -> "**Link Text**"
       *          "[Link Text]( )"      -> "**Link Text**"
       */
      .replace(/\[([^\]]+)\]\(\s*#?\s*\)/g, '**$1**')
  )
}
